import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/images/logo.png";
import locationIcon from "../assets/images/location.png";
import location2 from "../assets/images/location-modal/location.png";
import add from "../assets/images/location-modal/add.png";
import home from "../assets/images/location-modal/home.png";
import arrow from "../assets/images/location-modal/arrow-right.png";
import check from "../assets/images/location-modal/check.png";
import cancel from "../assets/images/navbar/cancel.png";
import search from "../assets/images/navbar/search.png";
import user from "../assets/images/navbar/user.png";
import bell from "../assets/images/navbar/bell.png";
import cart from "../assets/images/navbar/cart.png";
import whitebell from "../assets/images/navbar/white-bell.png";
import whitecart from "../assets/images/navbar/white-cart.png";
import Profile from "./Profile";
import AddAddressModal, { AddressData } from "../modal/AddAddressModal";
import LocationPickerModal from "../modal/LocationPickerModal";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { addressBookService, AddressBookData, searchService } from "../services/index";
import defaultImage from "../assets/images/cuisines/default.jpeg";
import { useDining } from "../context/DiningContext";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";
import {
  persistAddressDeliveryInstructions,
  joinDeliveryInstructions,
} from "../utils/helpers";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enableDining, toggleDining } = useDining();
  const isHomePage = location.pathname === "/";
  const showMobileBookTable = isHomePage && !enableDining;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<any | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isLocationOpen, setIsLocationOpen] = useState<boolean>(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);
  const [activeIcon, setActiveIcon] = useState<string | null>(null);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] =
    useState<boolean>(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] =
    useState<boolean>(false);
  const [selectedLocationData, setSelectedLocationData] = useState<any>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);
  
  // Refs for click-outside handling
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Address management state - now using API data
  const [addresses, setAddresses] = useState<AddressBookData[]>([]);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressBookData | null>(null);
  const [currentAddress, setCurrentAddress] = useState<string>(
    "Select your delivery location",
  );
  const [isLoadingAddresses, setIsLoadingAddresses] = useState<boolean>(false);

  // Use cart context instead of local state
  const { cartItems } = useCart();

  // Use auth context for authentication
  const { isAuthenticated, openSigninModal } = useAuth();

  // Use profile context for user location data
  const { profile } = useProfile();


  // Fetch addresses from API
  const fetchAddresses = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoadingAddresses(true);
    try {
      const response: any = await addressBookService.getAllAddresses();
      if (response.status && response.data) {
        setAddresses(response.data);

        // Find and set the default address
        const defaultAddr = response.data.find(
          (addr: AddressBookData) => addr.isDefault,
        );
        if (defaultAddr) {
          setSelectedAddress(defaultAddr);
          const displayAddress = `${defaultAddr.addressLine1}${defaultAddr.addressLine2 ? ", " + defaultAddr.addressLine2 : ""}, ${defaultAddr.city}, ${defaultAddr.state}`;
          setCurrentAddress(displayAddress);

          // Store in localStorage for order creation
          localStorage.setItem(
            "selectedDeliveryAddress",
            JSON.stringify(defaultAddr),
          );
        } else if (response.data.length > 0) {
          // If no default, use the first address
          const firstAddr = response.data[0];
          setSelectedAddress(firstAddr);
          const displayAddress = `${firstAddr.addressLine1}${firstAddr.addressLine2 ? ", " + firstAddr.addressLine2 : ""}, ${firstAddr.city}, ${firstAddr.state}`;
          setCurrentAddress(displayAddress);
          localStorage.setItem(
            "selectedDeliveryAddress",
            JSON.stringify(firstAddr),
          );
        } else if (profile?.city && profile?.state) {
          // If no saved addresses, use profile location
          const profileLocation = `${profile.city}, ${profile.state}`;
          setCurrentAddress(profileLocation);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [isAuthenticated, profile]);

  // Load saved address data from API on mount
  useEffect(() => {
    fetchAddresses();
  }, [isAuthenticated, fetchAddresses]);

  // Update current address when profile location changes
  useEffect(() => {
    if (profile?.city && profile?.state && addresses.length === 0) {
      const profileLocation = `${profile.city}, ${profile.state}`;
      setCurrentAddress(profileLocation);
    }
  }, [profile, addresses]);

  // Close profile modal when route changes
  useEffect(() => {
    setIsUserModalOpen(false);
  }, [location.pathname]);

  // Handle address selection
  const handleSetDefaultAddress = async (addressId: string) => {
    try {
      const response: any =
        await addressBookService.setDefaultAddress(addressId);
      if (response.status) {
        // Refresh addresses to get updated default status
        await fetchAddresses();
        setIsLocationOpen(false);
      }
    } catch (error) {
      console.error("Error setting default address:", error);
      alert("Failed to set default address. Please try again.");
    }
  };

  // Handle location selection from map
  const handleLocationSelect = (locationData: any) => {
    console.log("Navbar - handleLocationSelect called with:", locationData);
    setSelectedLocationData(locationData);
    setIsLocationPickerOpen(false);
    setIsLocationOpen(false);
    // Open the form modal with pre-filled location data
    setIsAddAddressModalOpen(true);
    console.log(
      "Navbar - About to open AddAddressModal, selectedLocationData will be:",
      locationData,
    );
  };

  // Handle saving new address from modal
  const handleSaveAddress = async (addressData: AddressData) => {
    try {
      const createData = {
        fullName: addressData.fullName,
        phone: addressData.phone,
        addressLine1: addressData.addressLine1,
        addressLine2: addressData.addressLine2,
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pincode,
        geoLocation: addressData.geoLocation,
        isDefault: addresses.length === 0, // Set as default if it's the first address
        deliveryInstructions: joinDeliveryInstructions(
          addressData.deliveryInstructions
        ),
      };

      const response: any = await addressBookService.createAddress(createData);
      if (response.status && response.data) {
        persistAddressDeliveryInstructions(
          response.data._id,
          addressData.deliveryInstructions
        );
        // Refresh addresses list
        await fetchAddresses();
        setIsAddAddressModalOpen(false);
        setSelectedLocationData(null);
      }
    } catch (error) {
      console.error("Error saving address:", error);
      alert("Failed to save address. Please try again.");
    }
  };

  const handleProfileClick = () => {
    if (isAuthenticated) {
      setIsUserModalOpen(true);
    } else {
      openSigninModal();
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingLocation(true);
    console.log("Requesting current location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        console.log("Location received:", {
          latitude,
          longitude,
          accuracy: `${accuracy.toFixed(2)} meters`,
          timestamp: new Date(position.timestamp).toLocaleString(),
        });

        try {
          // Using OpenStreetMap Nominatim API for reverse geocoding
          // Zoom level 16 gives better area-level accuracy than 18
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`;
          console.log("Fetching address from:", url);
          console.log(
            "Google Maps link for verification:",
            `https://www.google.com/maps?q=${latitude},${longitude}`,
          );

          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              "User-Agent": "Duddus Food Delivery App",
            },
          });

          console.log("Response status:", response.status);

          if (response.ok) {
            const data = await response.json();
            console.log("Full address data:", data);

            // Build a more accurate and readable address
            let address = "";

            if (data.address) {
              const parts = [];

              // Priority order for address parts
              const addr = data.address;

              // Building/house number + road
              if (addr.house_number && addr.road) {
                parts.push(`${addr.house_number} ${addr.road}`);
              } else if (addr.road) {
                parts.push(addr.road);
              }

              // Neighborhood/suburb/locality
              if (addr.neighbourhood) parts.push(addr.neighbourhood);
              else if (addr.suburb) parts.push(addr.suburb);
              else if (addr.locality) parts.push(addr.locality);

              // City/town/village
              if (addr.city) parts.push(addr.city);
              else if (addr.town) parts.push(addr.town);
              else if (addr.village) parts.push(addr.village);

              // State
              if (addr.state) parts.push(addr.state);

              // Use constructed address or fall back to display_name
              address = parts.length > 0 ? parts.join(", ") : data.display_name;
            } else {
              address =
                data.display_name ||
                `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            }

            console.log("Final address:", address);
            console.log(
              "Accuracy warning:",
              accuracy > 100
                ? "Low accuracy - position may be approximate"
                : "Good accuracy",
            );

            // Just display the detected location temporarily
            setCurrentAddress(address);
            alert(
              "Location detected! Please add this address from 'Add New Address' to save it.",
            );
            setIsLocationOpen(false);
          } else {
            console.log("Response not OK, using coordinates");
            setCurrentAddress(
              `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            );
            setIsLocationOpen(false);
          }
        } catch (error) {
          console.error("Error fetching address:", error);
          setCurrentAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          setIsLocationOpen(false);
        } finally {
          setIsLoadingLocation(false);
          console.log("Location fetch completed");
        }
      },
      (error) => {
        console.error("Geolocation error:", error.code, error.message);
        let errorMessage = "Unable to retrieve your location. ";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Permission denied. Please allow location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out. Please try again.";
            break;
          default:
            errorMessage += "An unknown error occurred.";
        }

        alert(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout to 15 seconds
        maximumAge: 0, // Don't use cached position
      },
    );
  };

  // Pages where top navbar is hidden on mobile but bottom nav still shows
  const hideTopNavOnMobile =
    location.pathname === "/notification" ||
    location.pathname === "/orders" ||
    location.pathname === "/support/chat";

  // Debounced search effect - triggers API call when user types
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await searchService.search(searchQuery.trim(), 5, 5);
        if (res && res.status) {
          setSearchResults(res.data);
        } else {
          setSearchError("No results found");
        }
      } catch (err: any) {
        console.error("Search error:", err);
        setSearchError(err.message || "Error fetching search results");
      } finally {
        setSearchLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside search container and not on the dropdown
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(target) &&
        // Also check if the click is not on the dropdown results
        !target.closest('[data-search-dropdown="true"]')
      ) {
        setSearchQuery("");
        setSearchResults(null);
        setSearchError(null);
      }
    };

    if (searchQuery) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchQuery]);

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchQuery("");
    navigate(`/result?keyword=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-[1000] p-0 bg-white dark:bg-gray-800 transition-colors duration-300">
        {/* Mobile Layout - Hide on notification and orders pages */}
        {!hideTopNavOnMobile && (
          <div className="md:hidden">
            {/* First Row: Location + Notification */}
            <div className="px-5 py-3 flex justify-between items-center">
              {/* Location Selector */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex w-10 h-10 p-2 justify-center items-center flex-shrink-0 rounded-full bg-white/25">
                  <img
                    src={locationIcon}
                    alt="location"
                    onClick={() => setIsLocationOpen(!isLocationOpen)}
                    className="cursor-pointer w-5 h-5 dark:invert"
                  />
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center">
                    <span className="text-gray-900 dark:text-white text-sm font-medium leading-[150%]">
                      {selectedAddress?.fullName || "Your Location"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="flex-shrink-0"
                    >
                      <path
                        d="M5 7.5L9.41074 11.9107C9.68852 12.1885 9.82741 12.3274 10 12.3274C10.1726 12.3274 10.3115 12.1885 10.5893 11.9107L15 7.5"
                        stroke="#141B34"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="overflow-hidden text-gray-600 dark:text-gray-400 text-xs font-normal leading-[160%] truncate">
                    {currentAddress}
                  </span>
                </div>
              </div>
              {/* Notification Icon */}
              <div
                className="h-10 w-10 rounded-full cursor-pointer flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor:
                    activeIcon === "bell" ? "#7402A7" : "transparent",
                }}
                onClick={() => {
                  setActiveIcon("bell");
                  navigate("/notification");
                }}
              >
                <img
                  src={activeIcon === "bell" ? whitebell : bell}
                  alt="Notifications"
                  className={`${activeIcon === "bell" ? "h-5 w-5" : "h-10 w-10 dark:invert"}`}
                />
              </div>
            </div>
            {/* Second Row: Search Bar + Book Table (mobile) */}
            <div className="px-4 pb-3 flex items-center gap-2">
              <div className="relative flex-1 min-w-0" ref={searchContainerRef}>
                <form
                  className="flex w-full min-w-0 px-3 py-3 items-center gap-2 rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"
                  onSubmit={handleSearch}
                >
                  <img src={search} alt="search" className="h-[14px] w-[14px] flex-shrink-0 dark:invert" />
                  <input
                    type="text"
                    placeholder="Restaurant name or dish..."
                    className="flex-1 min-w-0 border-none p-0 text-sm outline-none bg-transparent placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <img
                      src={cancel}
                      alt="cancel"
                      className="h-[14px] w-[14px] flex-shrink-0 cursor-pointer hover:opacity-70 dark:invert"
                      onClick={(e) => {
                        e.preventDefault();
                        setSearchQuery("");
                      }}
                    />
                  )}
                </form>

              {/* Mobile Search Dropdown */}
              {searchQuery && (
                <div className="md:hidden absolute left-0 right-0 top-[calc(100%+4px)] z-[999] bg-white dark:bg-gray-800 rounded-[10px] shadow-[0_8px_17px_0_rgba(0,0,0,0.20)] overflow-hidden" data-search-dropdown="true">
                  {/* Header - Fixed */}
                  <div className="flex justify-between items-center p-4 pb-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-[#6A6A6A] font-urbanist text-[12px] font-medium leading-[140%]">
                      Search Results
                    </p>
                  </div>
                  
                  {/* Scrollable Content Area */}
                  <div className="max-h-[50vh] overflow-y-auto p-4">
                    {searchLoading && (
                      <div className="text-center py-4 text-gray-500">Loading...</div>
                    )}
                    {searchError && (
                      <div className="text-center py-4 text-red-500">{searchError}</div>
                    )}
                    {searchResults && (
                      <div className="space-y-3">
                      {/* Dishes */}
                      {searchResults.dishes && searchResults.dishes.length > 0 && (
                        <div>
                          <p className="font-semibold text-sm mb-2 text-[#7402A7]">Dishes</p>
                          <div className="space-y-1">
                            {searchResults.dishes.map((dish: any, index: number) => (
                              <div
                                key={dish._id}
                                className={`flex items-center py-2 px-2 gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded ${index < searchResults.dishes.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}
                                onClick={() => {
                                  setSearchQuery("");
                                  setSearchResults(null);
                                  setSearchError(null);
                                  navigate(`/restaurants/${dish.restaurantId}?dish=${dish._id}`);
                                }}
                              >
                                <img
                                  src={dish.images?.[0]?.url || defaultImage}
                                  alt={dish.dishName}
                                  className="h-12 w-12 rounded object-cover bg-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.src = defaultImage;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 dark:text-white font-medium text-sm truncate">{dish.dishName}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{dish.restaurant?.restaurantName || dish.restaurant?.businessName}</p>
                                  <p className="text-xs text-green-700 dark:text-green-400 font-semibold mt-0.5">₹{dish.basePrice}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Restaurants */}
                      {searchResults.restaurants && searchResults.restaurants.length > 0 && (
                        <div className={searchResults.dishes && searchResults.dishes.length > 0 ? 'pt-1 border-t border-gray-200 dark:border-gray-700' : ''}>
                          <p className="font-semibold text-sm mb-2 text-[#7402A7]">Restaurants</p>
                          <div className="space-y-1">
                            {searchResults.restaurants.map((rest: any, index: number) => (
                              <div
                                key={rest.restaurantId}
                                className={`flex items-center py-2 px-2 gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded ${index < searchResults.restaurants.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}
                                onClick={() => {
                                  setSearchQuery("");
                                  setSearchResults(null);
                                  setSearchError(null);
                                  navigate(`/restaurants/${rest.restaurantId}`);
                                }}
                              >
                                <img
                                  src={rest.kitchenPhotos?.[0] || defaultImage}
                                  alt={rest.businessName}
                                  className="h-12 w-12 rounded object-cover bg-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.src = defaultImage;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 dark:text-white font-medium text-sm truncate">{rest.businessName || rest.restaurantName}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{rest.restaurantCategory || rest.location?.city}</p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{rest.isOpen ? '🟢 Open' : '🔴 Closed'}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* No results */}
                      {(!searchResults.dishes?.length && !searchResults.restaurants?.length) && (
                        <div className="text-center py-4 text-gray-500">No results found.</div>
                      )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>

              {showMobileBookTable && (
                <button
                  type="button"
                  onClick={toggleDining}
                  className="flex-shrink-0 flex items-center justify-center gap-1.5 h-11 px-3 rounded-xl bg-[#7402A7] text-white font-urbanist text-xs font-semibold whitespace-nowrap"
                >
                  Book Table
                  <span className="text-base leading-none" aria-hidden="true">
                    🪑
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Desktop Layout */}
        <div className="hidden md:block">
          <div className="max-w-[1400px] mx-auto px-5 py-3 flex justify-between items-center gap-5">
            <div className="flex items-center gap-2">
              {/* Logo */}
              <Link to="/" className="">
                <img src={logo} alt="Duddus Logo" className="w-[146px] h-12" />
              </Link>
              <div
                className="flex w-[193px] items-center relative gap-2"
                onClick={() => setIsLocationOpen(!isLocationOpen)}
              >
                <div className="flex w-10 h-10 p-2.5 justify-center items-center gap-2.5 flex-shrink-0 rounded-full bg-white/25">
                  <img
                    src={locationIcon}
                    alt="location"
                    className="cursor-pointer dark:invert"
                  />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <span className="text-gray-900 dark:text-white text-center text-base font-medium leading-[150%]">
                      {selectedAddress?.fullName || "Your Location"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L9.41074 11.9107C9.68852 12.1885 9.82741 12.3274 10 12.3274C10.1726 12.3274 10.3115 12.1885 10.5893 11.9107L15 7.5"
                        stroke="#141B34"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <span className="w-[160px] overflow-hidden text-gray-600 dark:text-gray-400 text-sm font-normal leading-[160%] truncate">
                    {currentAddress}
                  </span>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative flex-1 max-w-[390px] min-w-[200px]" ref={searchContainerRef}>
              <form
                className="flex px-4 py-3 items-center gap-3 rounded-2xl border border-[#7402A7] bg-white dark:bg-gray-700 shadow-sm"
                onSubmit={handleSearch}
              >
                <img src={search} alt="search" className="h-[14px] w-[14px] dark:invert" />
                <input
                  type="text"
                  placeholder="Search restaurants or food..."
                  className="flex-1 border-none p-0 text-sm outline-none bg-transparent placeholder-gray-600 dark:placeholder-gray-400 text-gray-900 dark:text-white"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <img
                    src={cancel}
                    alt="cancel"
                    className="h-[14px] w-[14px] cursor-pointer hover:opacity-70 dark:invert"
                    onClick={(e) => {
                      e.preventDefault();
                      setSearchQuery("");
                    }}
                  />
                )}
              </form>
              
              {/* Desktop Search Dropdown */}
              {searchQuery && (
                <div className="hidden md:block absolute left-0 right-0 top-[calc(100%+4px)] z-[999] bg-white dark:bg-gray-800 rounded-[10px] shadow-[0_8px_17px_0_rgba(0,0,0,0.20)] overflow-hidden" data-search-dropdown="true">
                  {/* Header - Fixed */}
                  <div className="flex justify-between items-center p-4 pb-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-[#6A6A6A] font-urbanist text-[12px] font-medium leading-[140%]">
                      Search Results
                    </p>
                  </div>
                  
                  {/* Scrollable Content Area */}
                  <div className="max-h-[500px] overflow-y-auto p-4">
                    {searchLoading && (
                      <div className="text-center py-4 text-gray-500">Loading...</div>
                    )}
                    {searchError && (
                      <div className="text-center py-4 text-red-500">{searchError}</div>
                    )}
                    {searchResults && (
                      <div className="space-y-3">
                      {/* Dishes */}
                      {searchResults.dishes && searchResults.dishes.length > 0 && (
                        <div>
                          <p className="font-semibold text-sm mb-2 text-[#7402A7]">Dishes</p>
                          <div className="space-y-1">
                            {searchResults.dishes.map((dish: any, index: number) => (
                              <div
                                key={dish._id}
                                className={`flex items-center py-2 px-2 gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded ${index < searchResults.dishes.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}
                                onClick={() => {
                                  setSearchQuery("");
                                  setSearchResults(null);
                                  setSearchError(null);
                                  navigate(`/restaurants/${dish.restaurantId}?dish=${dish._id}`);
                                }}
                              >
                                <img
                                  src={dish.images?.[0]?.url || defaultImage}
                                  alt={dish.dishName}
                                  className="h-12 w-12 rounded object-cover bg-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.src = defaultImage;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 dark:text-white font-medium text-sm truncate">{dish.dishName}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{dish.restaurant?.restaurantName || dish.restaurant?.businessName}</p>
                                  <p className="text-xs text-green-700 dark:text-green-400 font-semibold mt-0.5">₹{dish.basePrice}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Restaurants */}
                      {searchResults.restaurants && searchResults.restaurants.length > 0 && (
                        <div className={searchResults.dishes && searchResults.dishes.length > 0 ? 'pt-1 border-t border-gray-200 dark:border-gray-700' : ''}>
                          <p className="font-semibold text-sm mb-2 text-[#7402A7]">Restaurants</p>
                          <div className="space-y-1">
                            {searchResults.restaurants.map((rest: any, index: number) => (
                              <div
                                key={rest.restaurantId}
                                className={`flex items-center py-2 px-2 gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded ${index < searchResults.restaurants.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}
                                onClick={() => {
                                  setSearchQuery("");
                                  setSearchResults(null);
                                  setSearchError(null);
                                  navigate(`/restaurants/${rest.restaurantId}`);
                                }}
                              >
                                <img
                                  src={rest.kitchenPhotos?.[0] || defaultImage}
                                  alt={rest.businessName}
                                  className="h-12 w-12 rounded object-cover bg-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.src = defaultImage;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 dark:text-white font-medium text-sm truncate">{rest.businessName || rest.restaurantName}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{rest.restaurantCategory || rest.location?.city}</p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{rest.isOpen ? '🟢 Open' : '🔴 Closed'}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* No results */}
                      {(!searchResults.dishes?.length && !searchResults.restaurants?.length) && (
                        <div className="text-center py-4 text-gray-500">No results found.</div>
                      )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-[17px]">
              <div
                className="h-[48px] w-[48px] rounded-full cursor-pointer flex items-center justify-center"
                style={{
                  backgroundColor:
                    activeIcon === "bell" ? "#7402A7" : "transparent",
                }}
                onClick={() => {
                  if (activeIcon === "bell") {
                    setActiveIcon(null);
                    navigate("/");
                  } else {
                    setActiveIcon("bell");
                    navigate("/notification");
                  }
                }}
              >
                <img
                  src={activeIcon === "bell" ? whitebell : bell}
                  alt="Notifications"
                  className={`${activeIcon === "bell" ? "h-[22px] w-[22px]" : "h-[48px] w-[48px] dark:invert"}`}
                />
              </div>
              <div
                className="h-[48px] w-[48px] rounded-full cursor-pointer flex items-center justify-center relative"
                style={{
                  backgroundColor:
                    activeIcon === "cart" ? "#7402A7" : "transparent",
                }}
                onClick={() => {
                  if (activeIcon === "cart") {
                    setActiveIcon(null);
                    navigate("/");
                  } else {
                    setActiveIcon("cart");
                    navigate(`/restaurants/${cartItems?.[0]?.restaurantId}`);
                  }
                }}
              >
                <img
                  src={activeIcon === "cart" ? whitecart : cart}
                  alt="Cart"
                  className={`${activeIcon === "cart" ? "h-[22px] w-[22px]" : "h-[48px] w-[48px] dark:invert"}`}
                />
                {cartItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItems.length}
                  </span>
                )}
              </div>
              <div
                className="h-[48px] w-[48px] rounded-full cursor-pointer flex items-center justify-center"
                onClick={handleProfileClick}
              >
                <img src={user} alt="User" className="h-[48px] w-[48px] dark:invert" />
              </div>
            </div>
          </div>
        </div>

        {isLocationOpen && (
          <div
            className="fixed inset-0 z-50"
            onClick={() => setIsLocationOpen(false)}
          >
            <div
              className="absolute left-5 right-5 md:left-[217px] md:right-auto top-[80px] md:top-[33px] mt-2 w-auto md:w-[430px] bg-white dark:bg-gray-800 rounded-[20px] shadow-[0_8px_17px_0_rgba(0,0,0,0.20)] p-5 space-y-5"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Use Current Location */}
              <div
                className="flex justify-between items-center border dark:border-gray-700 rounded-[16px] p-[12px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={handleGetCurrentLocation}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={location2}
                    alt="location"
                    className="h-[16px] w-[16px] dark:invert"
                  />
                  <span className="text-purple-700 font-medium">
                    {isLoadingLocation
                      ? "Getting location..."
                      : "Use Current location"}
                  </span>
                </div>
                <img src={arrow} alt="arrow" className="h-[18px] w-[18px] dark:invert" />
              </div>

              {/* Address List Card */}
              <div className="border dark:border-gray-700 rounded-[16px] p-[12px] space-y-4">
                {isLoadingAddresses ? (
                  <div className="text-center py-4 text-gray-500">
                    Loading addresses...
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No saved addresses yet. Add your first address below!
                  </div>
                ) : (
                  addresses.map((address, index) => (
                    <React.Fragment key={address._id}>
                      {index > 0 && (
                        <div className="border-t border-dashed"></div>
                      )}
                      <div className="flex justify-between items-center">
                        <div className="flex gap-3 items-center">
                          <img
                            src={home}
                            alt="location"
                            className="h-[20px] w-[20px] dark:invert"
                          />
                          <div>
                            <p className="text-[14px] font-medium text-black dark:text-white">
                              {address.fullName}
                            </p>
                            <p className="w-[210px] md:w-[250px] overflow-hidden text-ellipsis whitespace-nowrap text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-normal leading-[140%]">
                              {address.addressLine1}
                              {address.addressLine2
                                ? ", " + address.addressLine2
                                : ""}
                              , {address.city}, {address.state}
                            </p>
                          </div>
                        </div>
                        {address.isDefault ? (
                          <img
                            src={check}
                            alt="selected"
                            className="h-[24px] w-[24px] dark:invert"
                          />
                        ) : (
                          <button
                            onClick={() =>
                              handleSetDefaultAddress(address._id!)
                            }
                            className="text-[#7402A7] font-urbanist text-[10px] font-semibold leading-[140%] hover:opacity-80 transition-opacity"
                          >
                            SET DEFAULT
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                  ))
                )}
              </div>

              {/* Add New Address */}
              <div
                className="flex justify-between items-center border dark:border-gray-700 rounded-[16px] px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => {
                  setIsLocationOpen(false);
                  setIsLocationPickerOpen(true);
                }}
              >
                <div className="flex items-center gap-3">
                  <img src={add} alt="add" className="h-[16px] w-[16px] dark:invert" />
                  <span className="text-[#7604A5] font-urbanist text-[16px] font-medium leading-[160%]">
                    Add New Address
                  </span>
                </div>
                <img src={arrow} alt="arrow" className="h-[18px] w-[18px] dark:invert" />
              </div>
            </div>
          </div>
        )}

        {/* User Modal - Slide from Right */}
        {isUserModalOpen && isAuthenticated && (
          <Profile setIsUserModalOpen={setIsUserModalOpen} />
        )}

        {/* Location Picker Modal - Opens First */}
        <LocationPickerModal
          isOpen={isLocationPickerOpen}
          onClose={() => {
            setIsLocationPickerOpen(false);
            // Don't clear selectedLocationData here - it needs to persist for AddAddressModal
          }}
          onLocationSelect={handleLocationSelect}
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        />

        {/* Add Address Form Modal - Opens After Location Selection */}
        <AddAddressModal
          isOpen={isAddAddressModalOpen}
          onClose={() => {
            setIsAddAddressModalOpen(false);
            setSelectedLocationData(null);
          }}
          onSave={handleSaveAddress}
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          initialLocationData={selectedLocationData}
        />
      </nav>

      {/* Mobile Cart Banner - Shows above bottom nav when cart has items (except on restaurant details page) */}
      {cartItems.length > 0 &&
        !location.pathname.startsWith("/order-summary") && (
          <div className="md:hidden fixed bottom-[57px] left-0 right-0 z-[999] px-4 pb-3 text-white font-urbanist">
            <div
              onClick={() => {
                const restaurantId = cartItems[0]?.restaurantId;
                if (restaurantId) {
                  navigate(`/order-summary`);
                }
              }}
              className="bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded-2xl p-4 shadow-lg cursor-pointer active:scale-95 transition-transform duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="">
                    <img
                      src={cartItems[0]?.restaurant?.kitchenPhotos?.[0] ?? ""}
                      alt="restaurant"
                      className="h-[45px] w-[54px] rounded-xl"
                    />
                  </div>
                  <div className="">
                    {!location.pathname.startsWith("/restaurants/") ? (
                      <p className="text-[12px]">
                        {cartItems[0]?.restaurant?.businessName}
                      </p>
                    ) : (
                      ""
                    )}
                    <p className=" text-[15px] font-semibold">
                      {cartItems.length === 1
                        ? "1 item added"
                        : `${cartItems.length} items added`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <p>View Cart</p>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="flex-shrink-0"
                  >
                    <path
                      d="M8.91003 19.92L15.43 13.4C16.2 12.63 16.2 11.37 15.43 10.6L8.91003 4.07999"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Mobile Bottom Navigation - Outside nav for proper fixed positioning */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-100 dark:bg-gray-800 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] z-[1000] transition-colors duration-300 border-t">
        <div className="flex justify-around items-center px-5 py-3">
          {/* Home */}
          <div
            onClick={() => {
              setIsUserModalOpen(false);
              navigate("/");
            }}
            className={`flex items-center justify-center gap-2 cursor-pointer border rounded-[35px] transition-all ${
              location.pathname === "/" && !isUserModalOpen ? "p-1" : ""
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                location.pathname === "/" && !isUserModalOpen ? "bg-[#7402A7]" : "bg-white"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.02 2.84016L3.63 7.04016C2.73 7.74016 2 9.23016 2 10.3602V17.7702C2 20.0902 3.89 21.9902 6.21 21.9902H17.79C20.11 21.9902 22 20.0902 22 17.7802V10.5002C22 9.29016 21.19 7.74016 20.2 7.05016L14.02 2.72016C12.62 1.74016 10.37 1.79016 9.02 2.84016Z"
                  stroke={location.pathname === "/" && !isUserModalOpen ? "white" : "#6A6A6A"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 17.99V14.99"
                  stroke={location.pathname === "/" && !isUserModalOpen ? "white" : "#6A6A6A"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {location.pathname === "/" && !isUserModalOpen && (
              <span className="text-[#7402A7] text-xs font-medium whitespace-nowrap">
                Home
              </span>
            )}
          </div>

          {/* Explore */}
          <div
            onClick={() => {
              setIsUserModalOpen(false);
              navigate("/restaurants");
            }}
            className={`flex items-center justify-center gap-2 cursor-pointer border rounded-[35px] transition-all ${
              location.pathname === "/restaurants" && !isUserModalOpen ? "px-2 py-1" : ""
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                location.pathname === "/restaurants" && !isUserModalOpen
                  ? "bg-[#7402A7]"
                  : "bg-white"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
                  stroke={
                    location.pathname === "/restaurants" && !isUserModalOpen ? "white" : "#6A6A6A"
                  }
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 22L20 20"
                  stroke={
                    location.pathname === "/restaurants" && !isUserModalOpen ? "white" : "#6A6A6A"
                  }
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {location.pathname === "/restaurants" && !isUserModalOpen && (
              <span className="text-[#7402A7] text-xs font-medium whitespace-nowrap">
                Explore
              </span>
            )}
          </div>

          {/* Orders */}
          {isAuthenticated && (
            <div
              onClick={() => {
                setIsUserModalOpen(false);
                navigate("/orders");
              }}
              className={`flex items-center justify-center gap-2 cursor-pointer border rounded-[35px] transition-all ${
                location.pathname === "/orders" && !isUserModalOpen ? "p-1" : ""
              }`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0 relative ${
                  location.pathname === "/orders" && !isUserModalOpen ? "bg-[#7402A7]" : "bg-white"
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 2H3.74001C4.82001 2 5.67 2.93 5.58 4L4.75 13.96C4.61 15.59 5.89999 16.99 7.53999 16.99H18.19C19.63 16.99 20.89 15.81 21 14.38L21.54 6.88C21.66 5.22 20.4 3.87 18.73 3.87H5.82001"
                    stroke={
                      location.pathname === "/orders" && !isUserModalOpen ? "white" : "#6A6A6A"
                    }
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.25 22C16.9404 22 17.5 21.4404 17.5 20.75C17.5 20.0596 16.9404 19.5 16.25 19.5C15.5596 19.5 15 20.0596 15 20.75C15 21.4404 15.5596 22 16.25 22Z"
                    stroke={
                      location.pathname === "/orders" && !isUserModalOpen ? "white" : "#6A6A6A"
                    }
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.25 22C8.94036 22 9.5 21.4404 9.5 20.75C9.5 20.0596 8.94036 19.5 8.25 19.5C7.55964 19.5 7 20.0596 7 20.75C7 21.4404 7.55964 22 8.25 22Z"
                    stroke={
                      location.pathname === "/orders" && !isUserModalOpen ? "white" : "#6A6A6A"
                    }
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 8H21"
                    stroke={
                      location.pathname === "/orders" && !isUserModalOpen ? "white" : "#6A6A6A"
                    }
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {location.pathname === "/orders" && !isUserModalOpen && (
                <span className="text-[#7402A7] text-xs font-medium whitespace-nowrap">
                  Orders
                </span>
              )}
            </div>
          )}

          {/* Profile */}
          <div
            onClick={handleProfileClick}
            className={`flex items-center justify-center gap-2 cursor-pointer border rounded-[35px] transition-all ${
              isUserModalOpen ? "p-1" : ""
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                isUserModalOpen ? "bg-[#7402A7]" : "bg-white"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                  stroke={isUserModalOpen ? "white" : "#6A6A6A"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22"
                  stroke={isUserModalOpen ? "white" : "#6A6A6A"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {isUserModalOpen && (
              <span className="text-[#7402A7] text-xs font-medium whitespace-nowrap">
                Profile
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
